import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { config } from '../config';
import axios from 'axios';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import { FaCalendarAlt, FaUser, FaClock, FaArrowRight, FaShareAlt, FaBookmark, FaChevronLeft } from 'react-icons/fa';
import { PixelContext } from './Context/PixelContext';
import { initFacebookPixel, trackEvent } from '../pixel';

const apiUrl = config.apiUrl;
const imageUrl = config.imageUrl;

const SingleBlog = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [isBookmarked, setIsBookmarked] = useState(false);

   const { pixel } = useContext(PixelContext);
    const pixelId = pixel?.pixel_id || null;

    useEffect(() => {
        if (pixelId) {
            initFacebookPixel(pixelId);
            trackEvent('ContentView');
        }
    }, [pixelId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${apiUrl}/blogs/${slug}`);
        const blogPost = response.data.blog;
        setPost(blogPost);

        const allPostsResponse = await axios.get(`${apiUrl}/blogs`);
        const allPosts = allPostsResponse.data.blogs;

        const filteredPosts = allPosts.filter(
          relatedPost => relatedPost.category_id === blogPost.category_id && 
          relatedPost.id !== blogPost.id
        ).slice(0, 3);
        setRelatedPosts(filteredPosts);

        const userResponse = await axios.get(`${apiUrl}/users`);
        const users = userResponse.data;
        const filteredUser = users.find(user => user.id === blogPost.author_id);
        setUser(filteredUser || {});

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: 'Check out this interesting blog post',
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 bg-white rounded-xl shadow-md">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Post Not Found</h2>
          <Link 
            to="/blog" 
            className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
          >
            <FaChevronLeft className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gray-50"
      >
        {/* Full-width Featured Image */}
        <div className="w-full h-auto max-h-[500px] overflow-hidden pt-32">
          <img
            src={`${imageUrl}/${post.image}`}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Container */}
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          {/* Article Header */}
          <motion.header 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <div className="flex flex-wrap items-center justify-between mb-6">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <div className="flex items-center text-gray-600">
                  <FaUser className="mr-2 text-green-500" />
                  <span>{user.name || 'Unknown Author'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FaCalendarAlt className="mr-2 text-green-500" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FaClock className="mr-2 text-green-500" />
                  <span>{post.read_time || '5'} min read</span>
                </div>
              </div>
              <div className="flex space-x-4">
                <button 
                  onClick={handleShare}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Share post"
                >
                  <FaShareAlt className="text-gray-600" />
                </button>
                <button 
                  onClick={() => setIsBookmarked(!isBookmarked)}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Bookmark post"
                >
                  <FaBookmark className={isBookmarked ? 'text-green-500 fill-current' : 'text-gray-600'} />
                </button>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight text-gray-900">
              {post.title}
            </h1>
            
            {post.tags && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.split(',').map((tag, index) => (
                  <span 
                    key={index} 
                    className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                  >
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}
          </motion.header>

          {/* Article Content */}
          <motion.article 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose max-w-none prose-lg text-gray-700 mb-16"
            dangerouslySetInnerHTML={{ __html: post.content }}
          ></motion.article>

          {/* Author Bio */}
          {user && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-sm p-6 mb-16 border border-gray-100"
            >
              <div className="flex flex-col md:flex-row items-center">
                <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden mb-4 md:mb-0 md:mr-6">
                  {user.avatar && (
                    <img 
                      src={`${imageUrl}/${user.avatar}`} 
                      alt={user.name} 
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Written by {user.name}</h3>
                  <p className="text-gray-600">{user.bio || 'Web Developer and Digital Marketer'}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mb-16"
            >
              <h2 className="text-2xl font-bold mb-8 text-gray-800 border-b pb-2">You might also like</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <motion.div
                    key={relatedPost.id}
                    whileHover={{ y: -5 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100"
                  >
                    <Link to={`/blog/${relatedPost.slug}`} className="block">
                      <div className="h-48 overflow-hidden">
                        <img
                          src={`${imageUrl}/${relatedPost.image}`}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold mb-2 text-gray-800 hover:text-green-600 transition-colors">
                          {relatedPost.title}
                        </h3>
                        <p className="text-gray-500 text-sm mb-4">
                          {formatDate(relatedPost.created_at)} • {relatedPost.read_time || '5'} min read
                        </p>
                        <div className="inline-flex items-center text-green-600 font-medium">
                          Read more <FaArrowRight className="ml-2" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Back to Blog Link */}
          <div className="text-center">
            <Link 
              to="/blog" 
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <FaChevronLeft className="mr-2" />
              Back to all articles
            </Link>
          </div>
        </div>
      </motion.main>
      
      <Footer />
    </>
  );
};

export default SingleBlog;